<template>
  <div>
    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      @shown="onShown"
      @hidden="onHidden"
    >
      <section
        class="invoice-preview-wrapper print-all-page"
        style="position: relative"
      >
        <b-alert
          variant="danger"
          :show="order === undefined"
        >
          <h4 class="alert-heading">
            Erreur lors de la récupération des bordereaux
          </h4>
          <div class="alert-body">
            Aucun bordereau trouvé. Consultez
            <b-link
              class="alert-link"
              :to="{ name: 'delivery-note-list' }"
            >
              la liste des bordereaux
            </b-link>
            pour d'autres bordereaux.
          </div>
        </b-alert>
        <div
          v-if="isLoading === true"
          class="text-center mb-2"
        >
          <b-spinner
            variant="primary"
            label="Text Centered"
          />
        </div>
        <b-row
          v-if="allOrders && allOrders.length > 0"
          class="invoice-preview"
        >
          <b-col
            cols="12"
            xl="9"
            md="8"
          >
            <b-card
              v-for="(orderItem, index) in allOrders"
              :id="orderItem.id"
              :key="orderItem.id"
              no-body
              class="invoice-preview-card pagebreak"
              :class="{ 'last-item': index === allOrders.length - 1, 'even-item': (index + 1) % 2 === 0 }"
            >
              <div class="bl-container">

                <!-- HEADER: Barcode left, logo center, barcode right -->
                <div class="bl-header">
                  <div class="bl-header-left">
                    <div class="bl-title">Bordereaux d'envoi</div>
                    <barcode
                      :value="headerLeftBarcodeValue(orderItem)"
                      :height="40"
                      :width="1.5"
                      :display-value="false"
                      format="CODE128"
                      class="bl-barcode"
                    />
                    <div class="bl-barcode-text">{{ headerLeftBarcodeValue(orderItem) }}</div>
                    <div class="bl-date">Date BLF : {{ formatDateOnly(orderItem.created_at) }}</div>
                  </div>
                  <div class="bl-header-center">
                    <img
                      src="/logo.png"
                      alt="Logo"
                      class="bl-logo"
                    >
                  </div>
                  <div class="bl-header-right">
                    <div
                      v-if="showHeaderOrderIdCodes(orderItem)"
                      class="bl-id-codes"
                    >
                      <div class="bl-id-qr-wrap">
                        <qrcode-vue
                          :value="headerBarcodeValue(orderItem)"
                          :size="90"
                          level="H"
                        />
                        <div class="bl-id-qr-number">{{ headerBarcodeValue(orderItem) }}</div>
                      </div>
                      <barcode
                        :value="headerBarcodeValue(orderItem)"
                        :height="55"
                        :width="1.8"
                        :display-value="true"
                        :font-size="20"
                        format="CODE128"
                        class="bl-id-barcode"
                      />
                    </div>
                    <div
                      v-if="orderItem.external_barcode_2"
                      class="bl-external-barcode2"
                    >
                      <barcode
                        :value="orderItem.external_barcode_2.toString()"
                        :height="35"
                        :width="1.2"
                        :display-value="false"
                        format="CODE128"
                        class="bl-barcode-small"
                      />
                      <div class="bl-external-id2-text">
                        {{ orderItem.external_id_2 || orderItem.external_barcode_2 }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- BON DE LIVRAISON N° -->
                <div class="bl-bon-title">
                  BON DE LIVRAISON N° {{ getDisplayId(orderItem) }}
                </div>

                <!-- ROUTE: origin >> dispatch >> destination -->
                <div class="bl-route">
                  <span class="bl-route-text">
                    {{ getSupplierGov(orderItem) }} >>------ dispatch ------>> {{ getDestinationRepo(orderItem) }}
                  </span>
                </div>

                <!-- EXCHANGE / FRAGILE BANNERS -->
                <div
                  v-if="orderItem.is_an_exchange"
                  class="bl-banner bl-banner-exchange"
                >
                  Échange
                </div>
                <div
                  v-if="orderItem.is_fragile"
                  class="bl-banner bl-banner-fragile"
                >
                  Fragile
                </div>

                <!-- EXPEDITEUR / DESTINATAIRE TABLE -->
                <table class="bl-parties-table">
                  <thead>
                    <tr>
                      <th>Expéditeur</th>
                      <th>Destinataire</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div class="bl-party-info">
                          <div><strong>{{ getSupplierPageName(orderItem) }}</strong></div>
                          <div><strong>Adresse :</strong> {{ getSupplierAddress(orderItem) }}</div>
                          <div v-if="getSupplierIdentityField(orderItem)"><strong>{{ getSupplierIdentityField(orderItem).label }} :</strong> {{ getSupplierIdentityField(orderItem).value }}</div>
                          <div v-if="getSupplierPhone(orderItem)"><strong>Tél. :</strong> {{ getSupplierPhone(orderItem) }}</div>
                        </div>
                      </td>
                      <td>
                        <div class="bl-party-info">
                          <div><strong>{{ orderItem.name }}</strong></div>
                          <div><strong>Tél. :</strong> {{ formatPhones(orderItem.phone1, orderItem.phone2) }}</div>
                          <div>
                            <strong>Adresse :</strong>
                            {{ orderItem.address || '' }}
                            {{ getDestinationGov(orderItem) }}
                            <span v-if="getDelegationName(orderItem)"> {{ getDelegationName(orderItem) }}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- PRODUCTS TABLE -->
                <table class="bl-products-table">
                  <thead>
                    <tr>
                      <th class="bl-col-designation">Désignation</th>
                      <th class="bl-col-qty">Quantité</th>
                      <th class="bl-col-total">Montant Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, idx) in orderItem.p"
                      :key="idx"
                    >
                      <td>{{ item.désignation }}</td>
                      <td class="text-center">{{ item.quantité }}</td>
                      <td
                        v-if="idx === 0"
                        :rowspan="orderItem.p.length || 1"
                        class="text-center bl-montant-cell"
                      >
                        {{ orderItem.price_ttc }}
                      </td>
                    </tr>
                    <tr v-if="orderItem.note">
                      <td
                        colspan="3"
                        class="bl-note-cell"
                      >
                        {{ orderItem.note }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- TOTAL TTC -->
                <div class="bl-total-line">
                  <strong>Total en TTC : {{ orderItem.price_ttc }} DT</strong>
                </div>

                <!-- FOOTER: Payment method + Ouvrir colis -->
                <table class="bl-footer-table">
                  <tr>
                    <td class="bl-footer-left">
                      <strong>{{ getPaymentMethodLabel(orderItem.payment_method) }}</strong>
                    </td>
                    <td class="bl-footer-right">
                      <strong>Ouvrir le colis :</strong> {{ orderItem.can_open_package ? 'Oui' : 'Non' }}
                    </td>
                  </tr>
                </table>

                <!-- COMPANY FOOTER -->
                <div
                  v-if="deliveryNotePrintNote"
                  class="bl-print-note"
                  dir="auto"
                >
                  {{ deliveryNotePrintNote }}
                </div>

                <div class="bl-company-footer">
                  <div>Adresse : {{ companyBordereauValue('address', company.address) }}</div>
                  <div>Matricule Fiscal : {{ companyBordereauValue('tax_identification_number', company.tax_identification_number || '') }}</div>
                </div>

              </div>
            </b-card>
          </b-col>

          <!-- Right Col: Card -->
          <b-col
            cols="12"
            md="4"
            xl="3"
            class="invoice-actions"
          >
            <b-card>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                :disabled="isLoading"
                variant="outline-secondary"
                class="mb-75"
                block
                @click="printInvoice"
              >
                Imprimer
              </b-button>
            </b-card>
          </b-col>
        </b-row>
      </section>
      <template #overlay>
        <div class="text-center">
          <b-icon
            icon="stopwatch"
            font-scale="3"
            animation="cylon"
          />
          <p id="cancel-label">
            Veuillez patienter...
          </p>
          Génération des bordereaux en cours
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BAlert,
  BLink,
  VBToggle,
  BSpinner,
  BOverlay,
  BIcon,
} from 'bootstrap-vue'
import VueBarcode from 'vue-barcode'
import QrcodeVue from 'qrcode.vue'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BAlert,
    BLink,
    barcode: VueBarcode,
    QrcodeVue,
    BSpinner,
    BOverlay,
    BIcon,
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ['orderIds'],
  data() {
    return {
      p: [],
      order: [],
      allOrders: [],
      company: {},
      deliveryNotePrintNote: '',
      isLoading: false,
      show: false,
    }
  },

  async created() {
    await this.loadCompanyInfo()
    await this.fetchDeliveryNotePrintNote()
    this.getOrders()
  },
  methods: {
    async loadCompanyInfo() {
      const company = await storeAuth.dispatch('fetchCompanyInfo')
      this.company = company || storeAuth.state.company || {}
    },
    async fetchDeliveryNotePrintNote() {
      try {
        const { data } = await axios.get('/api/core/parameters/delivery-note-print-note/config/')
        this.deliveryNotePrintNote = String((data && data.note) || '').trim()
      } catch (error) {
        this.deliveryNotePrintNote = ''
      }
    },
    companyBordereauValue(field, fallback = '') {
      const display = this.company
        && this.company.bordereau_display
        && this.company.bordereau_display[field]

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    getDisplayId(item) {
      if (item.display_cross_tenant_barcode && item.cross_tenant_barcode) {
        return item.cross_tenant_barcode
      }
      return item.id
    },
    normalizedBarcodeValue(value) {
      return String(value === undefined || value === null ? '' : value).trim()
    },
    deliveryNoteBarcodeMode(order) {
      const displayMode = order && order.delivery_note_barcode && order.delivery_note_barcode.mode
      if (displayMode) {
        return displayMode
      }

      const companyMode = (this.company && this.company.delivery_note_barcode_mode) || 'order_id_and_external_id'
      const isSyncRelatedOrder = Boolean(
        this.normalizedBarcodeValue(order && order.external_id)
          || (order && (order.is_outsourcing || order.outsourcing_company)),
      )
      return companyMode === 'external_id_only' && isSyncRelatedOrder
        ? companyMode
        : 'order_id_and_external_id'
    },
    headerBarcodeValue(order) {
      const display = order && order.delivery_note_barcode
      if (display && display.primary_value) {
        return this.normalizedBarcodeValue(display.primary_value)
      }

      const externalId = this.normalizedBarcodeValue(order && order.external_id)
      const orderId = this.normalizedBarcodeValue(order && order.id)
      if (this.deliveryNoteBarcodeMode(order) === 'external_id_only') {
        return externalId || orderId
      }

      return orderId
    },
    footerBarcodeValue(order) {
      const display = order && order.delivery_note_barcode
      if (display && display.secondary_value) {
        return this.normalizedBarcodeValue(display.secondary_value)
      }

      if (this.deliveryNoteBarcodeMode(order) === 'external_id_only') {
        return ''
      }

      return this.normalizedBarcodeValue(order && order.external_id)
    },
    headerLeftBarcodeValue(order) {
      return this.footerBarcodeValue(order) || this.headerBarcodeValue(order) || this.normalizedBarcodeValue(this.getDisplayId(order))
    },
    showHeaderOrderIdCodes(order) {
      return this.deliveryNoteBarcodeMode(order) !== 'external_id_only'
    },
    formatDateOnly(date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      const day1 = day ? day.substr(0, 2) : ''
      return `${year}-${month}-${day1}`
    },
    formatPhones(phone1, phone2) {
      const primary = phone1 || ''
      const secondary = phone2 || ''
      if (primary && secondary) return `${primary} / ${secondary}`
      return primary || secondary
    },
    getSupplierName(item) {
      if (!item || !item.supplier) return ''
      return item.supplier.username || ''
    },
    getSupplierPageName(item) {
      if (!item || !item.supplier) return ''
      return this.supplierBordereauValue(
        item.supplier,
        'username',
        item.supplier.nom_page || item.supplier.page_name || item.supplier.username || '',
      )
    },
    getSupplierAddress(item) {
      if (!item || !item.supplier) return ''
      return this.supplierBordereauValue(item.supplier, 'address', item.supplier.address || '')
    },
    getSupplierPhone(item) {
      if (!item || !item.supplier) return ''
      const p1 = item.supplier.phone || ''
      const p2 = item.supplier.phone2 || ''
      const fallback = p1 && p2 ? `${p1} / ${p2}` : p1 || p2
      return this.supplierBordereauValue(item.supplier, 'phone_number', fallback)
    },
    getSupplierTaxId(item) {
      if (!item || !item.supplier) return ''
      return item.supplier.tax_identification_number || ''
    },
    supplierBordereauValue(supplier, field, fallback = '') {
      const display = supplier
        && supplier.bordereau_display
        && supplier.bordereau_display[field]

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    supplierBordereauDisplay(supplier, field) {
      return supplier
        && supplier.bordereau_display
        && supplier.bordereau_display[field]
    },
    getSupplierIdentityField(item) {
      if (!item || !item.supplier) return null

      const { supplier } = item
      const fields = [
        {
          key: 'matricule_fiscale',
          label: 'M/F',
          fallback: supplier.tax_identification_number,
        },
        {
          key: 'cin',
          label: 'CIN',
          fallback: supplier.cin,
        },
      ]

      const visibleField = fields.find(field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        return display && display.show === true && (display.value || field.fallback)
      })

      const selectedField = visibleField || fields.find(field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        return display && display.show === false
      }) || fields.find(field => field.fallback)

      if (!selectedField) return null

      return {
        label: selectedField.label,
        value: this.supplierBordereauValue(supplier, selectedField.key, selectedField.fallback),
      }
    },
    getSupplierGov(item) {
      if (!item) return ''
      if (item.repository && typeof item.repository === 'object' && item.repository.name) {
        return item.repository.name
      }
      if (item.supplier && item.supplier.address) {
        return item.supplier.address
      }
      return ''
    },
    getDestinationGov(item) {
      if (!item) return ''
      if (typeof item.governorate === 'object' && item.governorate) {
        return item.governorate.name || ''
      }
      return item.governorate || ''
    },
    getDestinationRepo(item) {
      if (!item) return ''
      if (item.destination_repository_name) {
        return item.destination_repository_name
      }
      return this.getDestinationGov(item)
    },
    getDelegationName(item) {
      if (!item || !item.delegation) return ''
      if (typeof item.delegation === 'object') return item.delegation.name || ''
      return item.delegation
    },
    getPaymentMethodLabel(method) {
      const methods = {
        0: 'Espèce seulement',
        1: 'Par chèque',
        2: 'Par traite',
      }
      return methods[method] || 'Espèce seulement'
    },
    async getOrders() {
      this.isLoading = true
      try {
        // Parse order IDs from route parameter
        const orderIds = this.orderIds ? this.orderIds.split(',') : []

        if (orderIds.length === 0) {
          // eslint-disable-next-line no-console
          console.warn('No delivery notes selected')
          this.isLoading = false
          return
        }

        // Make single API call to get all orders with supplier details
        const response = await axios.post('/api/orders/bulk-delivery-notes/', {
          order_ids: orderIds,
          include_supplier_details: true,
        })

        if (response.data && response.data.orders) {
          this.allOrders = response.data.orders.map(order => ({
            ...order,
            p: order.processed_products || [],
          }))

          const currentUser = storeAuth.state.user
          if (orderIds.length > 0 && currentUser && currentUser.role === 'supplier') {
            await this.markOrdersAsPrinted(orderIds)
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching orders:', error)
        // eslint-disable-next-line no-console
        console.warn('Error loading delivery notes')
      } finally {
        this.isLoading = false
      }
    },
    async markOrdersAsPrinted(orderIds) {
      try {
        await axios.post('/api/orders/mark-orders-as-printed/', {
          order_ids: orderIds,
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error marking orders as printed:', error)
      }
    },
    onShown() {
      // Focus the cancel button when the overlay is showing
      // this.$refs.cancel.focus()
    },
    onHidden() {
      // Focus the show button when the overlay is removed
      // this.$refs.show.focus()
    },
  },
  setup() {
    const invoiceData = ref(null)
    const paymentDetails = ref({})

    const printInvoice = () => {
      window.print()
    }

    return {
      invoiceData,
      paymentDetails,
      printInvoice,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

.bl-container {
  padding: 20px 24px;
  padding-top: 55px;
  font-family: Arial, Helvetica, sans-serif;
  color: #000;
  font-size: 14px;
  line-height: 1.4;
}

.bl-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.bl-header-left {
  flex: 1;
  min-width: 0;
}

.bl-title {
  font-size: 15px;
  font-weight: 700;
  color: #000;
  margin-bottom: 4px;
}

.bl-barcode {
  display: block;
  max-width: 220px;
}

.bl-barcode-text {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 4px;
  margin-top: 6px;
}

.bl-date {
  font-size: 14px;
  font-weight: 600;
  margin-top: 22px;
  color: #000;
}

.bl-header-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  gap: 6px;
}

.bl-header-center-top {
  text-align: center;
}

.bl-external-barcode2 {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.bl-barcode-small {
  display: block;
  max-width: 220px;
}

.bl-external-id2-text {
  font-size: 16px;
  font-weight: 600;
  text-align: right;
  margin-top: 6px;
  letter-spacing: 4px;
}

.bl-package-count {
  font-size: 42px;
  font-weight: 700;
  color: #000;
  line-height: 1;
}

.bl-header-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  min-width: 0;
  padding-top: 0;
}

.bl-id-codes {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 6px;
  margin-top: 12px;
}

.bl-id-qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.bl-id-qr-number {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  text-align: center;
  color: #000;
}

.bl-id-barcode {
  display: block;

  svg {
    display: block !important;
    width: 260px !important;
    max-width: 260px !important;
    height: 90px !important;
    overflow: visible !important;
  }
}

.bl-logo {
  max-width: 140px;
  height: auto;
}

.bl-bon-title {
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #000;
  margin: 10px 0;
  letter-spacing: 0.5px;
}

.bl-route {
  text-align: center;
  margin: 8px 0 12px;
}

.bl-route-text {
  font-size: 18px;
  font-weight: 700;
  color: #000;
  letter-spacing: 0.5px;
}

.bl-banner {
  text-align: center;
  font-weight: 900;
  padding: 4px 0;
  margin: 4px 0;
  border: 2px solid #000;
  font-size: 18px;
}

.bl-banner-exchange {
  color: #0A2634;
}

.bl-banner-fragile {
  color: #BC2B18;
  font-size: 24px;
  border-color: #BC2B18;
}

.bl-parties-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
  border: 1.5px solid #000;

  th, td {
    border: 1.5px solid #000;
    padding: 10px 14px;
    vertical-align: top;
    font-size: 16px;
  }

  th {
    text-align: center;
    font-weight: 700;
    font-size: 17px;
    background-color: #f5f5f5;
  }
}

.bl-party-info {
  div {
    margin-bottom: 3px;
    line-height: 1.4;
  }

  strong {
    font-weight: 700;
  }
}

.bl-products-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 6px;
  border: 1.5px solid #000;

  th, td {
    border: 1.5px solid #000;
    padding: 8px 12px;
    font-size: 16px;
  }

  th {
    text-align: center;
    font-weight: 700;
    font-size: 17px;
    background-color: #f5f5f5;
  }

  .bl-col-designation {
    width: 55%;
  }

  .bl-col-qty {
    width: 20%;
    text-align: center;
  }

  .bl-col-total {
    width: 25%;
    text-align: center;
  }
}

.bl-montant-cell {
  font-weight: 700;
  font-size: 18px;
  vertical-align: middle;
}

.bl-note-cell {
  font-style: italic;
  font-size: 15px;
  color: #333;
}

.bl-total-line {
  text-align: right;
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin: 8px 0 12px;
  padding-right: 4px;
}

.bl-footer-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
  border: 1.5px solid #000;

  td {
    border: 1.5px solid #000;
    padding: 10px 14px;
    font-size: 16px;
    width: 50%;
  }
}

.bl-footer-left {
  font-weight: 700;
}

.bl-bottom-codes {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: 10px;
  padding-top: 6px;
}

.bl-bottom-barcode {
  text-align: center;
}

.bl-bottom-qr {
  text-align: center;
}

.bl-bottom-code-id {
  font-size: 11px;
  font-weight: 600;
  margin-top: 2px;
  text-align: center;
}

.bl-company-footer {
  font-size: 11px;
  color: #444;
  line-height: 1.4;
  margin-top: 6px;
  padding-top: 4px;
}

.bl-print-note {
  margin-top: 8px;
  padding: 6px 8px;
  border: 1px solid #222;
  border-radius: 2px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
  white-space: pre-wrap;
}
</style>

<style lang="scss">
@media print {
  @page {
    margin: 10mm 5mm 5mm 5mm !important;
    padding: 0 !important;
    size: A4 portrait;
  }

  * {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }

  html, body {
    height: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  body {
    background-color: transparent !important;
  }

  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  .customizer-toggle,
  footer.footer,
  .content-header {
    display: none !important;
  }

  .content.app-content {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }

  .b-overlay {
    display: none !important;
  }

  .invoice-preview-wrapper {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;

    .row.invoice-preview {
      width: 100% !important;
      margin: 0 !important;

      .col-md-8 {
        max-width: 100% !important;
        width: 100% !important;
        flex-grow: 1;
        padding: 0 !important;
      }
    }

    .invoice-actions {
      display: none !important;
    }
  }

  .pagebreak {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    height: 50vh !important;
    max-height: 50vh !important;
    min-height: 50vh !important;
    overflow: hidden !important;
    margin: 0 !important;
    border: none !important;

    &.even-item:not(.last-item) {
      page-break-after: always !important;
      break-after: page !important;
    }

    &.last-item {
      page-break-after: avoid !important;
      break-after: avoid !important;
      margin-bottom: 0 !important;
    }

    &:only-child,
    &:first-child:last-of-type {
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
  }

  .bl-container {
    padding: 12px 16px !important;
    height: 50vh !important;
    max-height: 50vh !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .bl-header {
    margin-bottom: 4px !important;
  }

  .bl-header-right {
    padding-top: 0 !important;
  }

  .bl-id-codes {
    gap: 6px !important;
    margin-bottom: 4px !important;
    margin-top: 6px !important;
  }

  .bl-id-qr-number {
    font-size: 11px !important;
    letter-spacing: 1px !important;
  }

  .bl-id-barcode svg {
    width: 200px !important;
    max-width: 200px !important;
    height: 65px !important;
  }

  .bl-title {
    font-size: 13px !important;
  }

  .bl-barcode {
    max-width: 180px !important;
  }

  .bl-barcode-text {
    font-size: 13px !important;
    letter-spacing: 3px !important;
    margin-top: 4px !important;
  }

  .bl-date {
    font-size: 11px !important;
    margin-top: 14px !important;
  }

  .bl-package-count {
    font-size: 36px !important;
  }

  .bl-logo {
    max-width: 110px !important;
  }

  .bl-bon-title {
    font-size: 15px !important;
    margin: 6px 0 !important;
  }

  .bl-route {
    margin: 4px 0 8px !important;
  }

  .bl-route-text {
    font-size: 14px !important;
  }

  .bl-banner {
    padding: 2px 0 !important;
    margin: 2px 0 !important;
    font-size: 14px !important;
  }

  .bl-parties-table {
    margin-bottom: 8px !important;

    th, td {
      padding: 5px 8px !important;
      font-size: 13px !important;
    }

    th {
      font-size: 14px !important;
    }
  }

  .bl-products-table {
    margin-bottom: 4px !important;

    th, td {
      padding: 4px 6px !important;
      font-size: 13px !important;
    }
  }

  .bl-montant-cell {
    font-size: 15px !important;
  }

  .bl-total-line {
    font-size: 16px !important;
    margin: 4px 0 8px !important;
  }

  .bl-footer-table {
    margin-bottom: 6px !important;

    td {
      padding: 5px 8px !important;
      font-size: 13px !important;
    }
  }

  .bl-company-footer {
    font-size: 9px !important;
    margin-top: 4px !important;
  }

  .bl-print-note {
    margin-top: 5px !important;
    padding: 4px 6px !important;
    font-size: 11px !important;
    line-height: 1.25 !important;
  }

  .bl-external-barcode2 {
    margin-top: 2px !important;
  }

  .bl-barcode-small {
    max-width: 180px !important;
  }

  .bl-external-id2-text {
    font-size: 13px !important;
    letter-spacing: 3px !important;
    margin-top: 4px !important;
  }

  .bl-bottom-codes {
    margin-top: 4px !important;
    padding-top: 2px !important;
    gap: 10px !important;
  }

  .bl-bottom-code-id {
    font-size: 9px !important;
  }

  html, body {
    height: auto !important;
  }

  .col-md-8 > *:last-child {
    page-break-after: avoid !important;
    break-after: avoid !important;
  }
}
</style>
